import type { AlgoliaProduct } from '@jane/search/types';

type ProductWeights = {
  price_each: string;
  price_eighth_ounce: string;
  price_gram: string;
  price_half_gram: string;
  price_half_ounce: string;
  price_ounce: string;
  price_quarter_ounce: string;
  price_two_gram: string;
};

type ProductDiscountWeights = {
  discounted_price_each: string;
  discounted_price_eighth_ounce: string;
  discounted_price_gram: string;
  discounted_price_half_gram: string;
  discounted_price_half_ounce: string;
  discounted_price_ounce: string;
  discounted_price_quarter_ounce: string;
  discounted_price_two_gram: string;
};

const getWeightLabel = (label: string) => {
  const weightLabel = label.split(' ').join('_');
  return weightLabel;
};

type ProductPrices = Pick<
  AlgoliaProduct,
  | 'available_weights'
  | 'price_each'
  | 'discounted_price_each'
  | 'price_eighth_ounce'
  | 'discounted_price_eighth_ounce'
  | 'price_half_ounce'
  | 'discounted_price_half_ounce'
  | 'price_ounce'
  | 'discounted_price_ounce'
  | 'price_quarter_ounce'
  | 'discounted_price_quarter_ounce'
  | 'price_gram'
  | 'discounted_price_gram'
  | 'price_half_gram'
  | 'discounted_price_half_gram'
  | 'price_two_gram'
  | 'discounted_price_two_gram'
>;

export const areAllDiscountsTheSamePercentage = (product: ProductPrices) => {
  let discountedAmount = null;
  let sameWeight = true;
  for (const weight of product.available_weights) {
    const weightLabel = getWeightLabel(weight);
    const discountedLabel =
      `discounted_price_${weightLabel}` as keyof ProductDiscountWeights;
    const priceLabel = `price_${weightLabel}` as keyof ProductWeights;

    const discountAmount =
      (Number(product[priceLabel]) - Number(product[discountedLabel])) /
      Number(product[priceLabel]);

    if (discountedAmount === null) {
      discountedAmount = discountAmount;
    } else {
      // just in case there's a rounding error
      const checkDiscount = Math.abs(discountAmount - discountedAmount);
      if (checkDiscount > 0.01) {
        sameWeight = false;
      }
    }
  }

  return sameWeight;
};

export const getSpecialBadgeLabelsForMultipleWeights = (
  product: ProductPrices
) => {
  const specialBadgeLabels: Record<string, string> = {};

  for (const weight of product.available_weights) {
    const weightLabel = getWeightLabel(weight);
    const discountedLabel =
      `discounted_price_${weightLabel}` as keyof ProductDiscountWeights;
    const priceLabel = `price_${weightLabel}` as keyof ProductWeights;

    const discountAmount = Math.round(
      ((Number(product[priceLabel]) - Number(product[discountedLabel])) /
        Number(product[priceLabel])) *
        100
    );

    if (discountAmount < 100) {
      specialBadgeLabels[weight] = `-${discountAmount}%`;
    }
  }

  return specialBadgeLabels;
};
